import { FormDatePicker, FormFileInput, FormInput, FormPhoneInput, FormSectionHeader, FormSelect, Loader } from "@/components";
import { CLOSING_CONTACT_EMAIL, CLOSING_CONTACT_NAME, CLOSING_CONTACT_PHONE, CLOSING_CONTACT_SOURCE, DIFFERENT_PERSON_CONTACT, OWNER, PAL_NEEDED_BY_DATE, PROOF_FILE, PROPERTY_ID, RECEIPT_DATE, RECEIPT_FROM, receivedFromOptions, REPORTER_CONTACT, REPORTER_EMAIL, REPORTER_NAME, REPORTER_PHONE, REPORTER_TITLE, SELLER_EMAIL, SELLER_NAME, SELLER_PHONE, TARGET_CLOSING_DATE } from "../../consts";
import useStep1 from "./useStep1";
import { Button, Card, CardContent, RadioGroup, RadioGroupItem } from "@/components/ui";
import { FormProvider } from "react-hook-form";
import { StepsProps } from "../types";

const Step1 = ({ onCancel, onSubmit: onBaseSubmit, isView, isAddPending }: StepsProps) => {
  const { isLoading, propertyList, formData, isReporter, form, onClosingContactChange,
    requiredFields, keys, onChangePhone, onChangeProperty, onSubmit
  } = useStep1({onBaseSubmit});
  const { errors } = form.formState;

  return (
    <>
      <Card className="shadow-none rounded-lg border-0">
        <CardContent>
          <FormSectionHeader className="text-[15px] font-semibold text-g-700">
            Intake Form
          </FormSectionHeader>
          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-[25px]">
                <FormSelect
                  key={keys.property}
                  name={PROPERTY_ID}
                  control={form.control}
                  label="Property"
                  placeholder="Please select"
                  options={propertyList?.map((data: any) => ({
                    value: data?.propertyId?.toString(),
                    label: data?.name,
                  }))}
                  disabled={isLoading || isView}
                  error={errors?.[PROPERTY_ID]?.message}
                  isRequired={requiredFields?.includes(PROPERTY_ID)}
                  onChange={onChangeProperty}
                />
                <FormInput
                  name={OWNER}
                  control={form.control}
                  label="Current Owner"
                  disabled
                />
                <FormDatePicker
                  name={RECEIPT_DATE}
                  control={form.control}
                  label="Receipt Date"
                  placeholder="dd-mm-yyyy"
                  error={errors?.[RECEIPT_DATE]?.message}
                  isRequired={requiredFields?.includes(RECEIPT_DATE)}
                  disabled={isView}
                />
                <FormSelect
                  name={RECEIPT_FROM}
                  control={form.control}
                  label="Received From"
                  options={receivedFromOptions}
                  placeholder="Please select"
                  error={errors?.[RECEIPT_FROM]?.message}
                  isRequired={requiredFields?.includes(RECEIPT_FROM)}
                  disabled={isView}
                />
                <FormInput
                  name={REPORTER_NAME}
                  control={form.control}
                  label="Reporter Name"
                  placeholder="Please enter"
                  error={errors?.[REPORTER_NAME]?.message}
                  isRequired={requiredFields?.includes(REPORTER_NAME)}
                  disabled={isView}
                  variant="char"
                  maxLength={50}
                />
                <FormInput
                  name={REPORTER_TITLE}
                  control={form.control}
                  label="Reporter Title"
                  placeholder="Please enter"
                  error={errors?.[REPORTER_TITLE]?.message}
                  isRequired={requiredFields?.includes(REPORTER_TITLE)}
                  disabled={isView}
                />
                <FormInput
                  name={REPORTER_EMAIL}
                  control={form.control}
                  label="Reporter Email"
                  placeholder="Please enter"
                  error={errors?.[REPORTER_EMAIL]?.message}
                  isRequired={requiredFields?.includes(REPORTER_EMAIL) || isReporter}
                  type="email"
                  disabled={isView}
                />
                <FormPhoneInput
                  name={REPORTER_PHONE}
                  control={form.control}
                  label="Reporter Phone"
                  placeholder="Please enter"
                  error={errors?.[REPORTER_PHONE]?.message}
                  isRequired={requiredFields?.includes(REPORTER_PHONE)}
                  disabled={isView}
                  onChange={onChangePhone}
                />
                <FormInput
                  name={SELLER_NAME}
                  control={form.control}
                  label="Seller Name"
                  placeholder="Please enter"
                  error={errors?.[SELLER_NAME]?.message}
                  isRequired={requiredFields?.includes(SELLER_NAME)}
                  disabled={isView}
                  variant="char"
                  maxLength={50}
                />
                <FormInput
                  name={SELLER_EMAIL}
                  control={form.control}
                  label="Seller Email"
                  placeholder="Please enter"
                  error={errors?.[SELLER_EMAIL]?.message}
                  isRequired={requiredFields?.includes(SELLER_EMAIL)}
                  type="email"
                  disabled={isView}
                />
                <FormPhoneInput
                  name={SELLER_PHONE}
                  control={form.control}
                  label="Seller Phone"
                  placeholder="Please enter"
                  error={errors?.[SELLER_PHONE]?.message}
                  isRequired={requiredFields?.includes(SELLER_PHONE)}
                  disabled={isView}
                />
                <FormDatePicker
                  name={TARGET_CLOSING_DATE}
                  control={form.control}
                  label="Target Closing Date"
                  placeholder="dd-mm-yyyy"
                  error={errors?.[TARGET_CLOSING_DATE]?.message}
                  isRequired={requiredFields?.includes(TARGET_CLOSING_DATE)}
                  disabled={isView}
                  minDate={formData[RECEIPT_DATE]}
                />
                <FormDatePicker
                  name={PAL_NEEDED_BY_DATE}
                  control={form.control}
                  label="PAL Needed By"
                  placeholder="dd-mm-yyyy"
                  error={errors?.[PAL_NEEDED_BY_DATE]?.message}
                  isRequired={requiredFields?.includes(PAL_NEEDED_BY_DATE)}
                  disabled={isView}
                />
                <FormFileInput
                  key={keys.proof}
                  name={PROOF_FILE}
                  control={form.control}
                  label="Intake Proof"
                  placeholder="Please enter"
                  errors={(errors as any)?.[PROOF_FILE]?.message}
                  isRequired={requiredFields?.includes(PROOF_FILE)}
                  disabled={isView}
                  maxSize={10}
                  accept=".doc,.docx,.csv,.png,.jpg,.jpeg,.txt,.pdf"
                />
              </div>
              <div className="mt-7">
                <FormSectionHeader className="text-[15px] font-semibold text-g-700">
                  Closing Contact Info
                </FormSectionHeader>
                <RadioGroup
                  className="flex flex-row gap-6"
                  value={formData?.[CLOSING_CONTACT_SOURCE]}
                  onValueChange={onClosingContactChange}
                  disabled={isView}
                >
                  <label className="flex cursor-pointer items-center gap-2 text-[13px] text-g-600">
                    <RadioGroupItem value={REPORTER_CONTACT} />
                    Reporter
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 text-[13px] text-g-600">
                    <RadioGroupItem value={DIFFERENT_PERSON_CONTACT} />
                    Different Person
                  </label>
                </RadioGroup>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[25px] mt-6">
                  <FormInput
                    name={CLOSING_CONTACT_NAME}
                    control={form.control}
                    label="Contact Name"
                    placeholder="Please enter"
                    error={errors?.[CLOSING_CONTACT_NAME]?.message}
                    isRequired={requiredFields?.includes(CLOSING_CONTACT_NAME) || !isReporter}
                    disabled={isView || isReporter}
                    variant="char"
                    maxLength={50}
                  />
                  <FormInput
                    name={CLOSING_CONTACT_EMAIL}
                    control={form.control}
                    label="Contact Email"
                    placeholder="Please enter"
                    error={errors?.[CLOSING_CONTACT_EMAIL]?.message}
                    isRequired={requiredFields?.includes(CLOSING_CONTACT_EMAIL) || !isReporter}
                    type="email"
                    disabled={isView || isReporter}
                  />
                  <FormPhoneInput
                    name={CLOSING_CONTACT_PHONE}
                    control={form.control}
                    label="Contact Phone"
                    placeholder="Please enter"
                    error={errors?.[CLOSING_CONTACT_PHONE]?.message}
                    isRequired={requiredFields?.includes(CLOSING_CONTACT_PHONE)}
                    disabled={isView || isReporter}
                  />
                </div>
              </div>
              <div className="mt-7 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="min-w-[110px]"
                  onClick={onCancel}
                  disabled={isAddPending}
                >
                  Cancel
                </Button>
                {isView ? <></> : <Button className="min-w-[90px]" disabled={isAddPending}>
                  {isAddPending ? <Loader variant="secondary" /> : <></>} Save & Next
                </Button>}
              </div>
            </form>
          </FormProvider>
        </CardContent>
      </Card>
    </>
  )
}

export default Step1;